/**
 * ROI engine: the C&D model of the current simulator (wrmarketing/portal,
 * public/ROI_Simulator.html and ROI_Simulator_FR.html, function
 * updateDisplay), ported verbatim (brief sections 7.7 and 10). The EN (USD)
 * and FR (EUR) calculators share the same maths; only labels and currency
 * differ (labels.ts).
 *
 * One deliberate change (decision of Oct 28, 2026): the figure the simulator
 * called "Year 10 ROI" counted 10 years of profit against N years of
 * maintenance, N being the depreciation period. It is now "ROI over N years"
 * and uses N on both sides (roiOverYears). Every other value is unchanged and
 * checked against the simulator in test/roi-engine.test.ts.
 *
 * The maths keep the simulator's divisions by zero (NaN, Infinity): the
 * display (format.ts) shows them as a dash, and a payback without profit as
 * "No payback", so zero sorters or zero robots never break a screen.
 */

export const ROI_CALCULATORS = {
  'C&D-Simulator': { name: 'C&D Simulator (EN - USD)', lang: 'en', currency: 'USD', symbol: '$' },
  'C&D-Simulator-FR': {
    name: 'C&D Simulator (FR - EUR)',
    lang: 'fr',
    currency: 'EUR',
    symbol: '€',
  },
} as const;

export type RoiCalculatorId = keyof typeof ROI_CALCULATORS;
export const ROI_CALCULATOR_IDS = Object.keys(ROI_CALCULATORS) as RoiCalculatorId[];

export function isRoiCalculator(id: unknown): id is RoiCalculatorId {
  return typeof id === 'string' && Object.hasOwn(ROI_CALCULATORS, id);
}

/** The numeric inputs, named as the simulator's fields (sortorsPerShift included). */
export const ROI_NUMBER_INPUTS = [
  'annualTonnage',
  'disposalCost',
  'operatingDays',
  'hoursPerDay',
  'sortorsPerShift',
  'pickerCost',
  'totalSorterTonsInput',
  'capexRobot',
  'numRobots',
  'depreciationPeriod',
  'maintenancePercent',
  'robotPicksPerMin',
  'robotKgPerPick',
  'sortersReplace',
  's10SortersCount',
  'extraHours',
] as const;

export type RoiNumberInput = (typeof ROI_NUMBER_INPUTS)[number];
export type RoiHoursMode = 'weekly' | 'total';

/**
 * The inputs as typed. A number field may hold a number or the text of the
 * field (an empty field is ''): the engine reads it as the simulator did.
 */
export type RoiInputs = Record<RoiNumberInput, number | string> & { hoursMode: RoiHoursMode };

/** The values a new project starts with: those of the simulator's page. */
export const ROI_DEFAULTS: RoiInputs = {
  annualTonnage: 62000,
  disposalCost: 52,
  operatingDays: 260,
  hoursPerDay: 9,
  sortorsPerShift: 10,
  pickerCost: 50000,
  totalSorterTonsInput: 22460,
  capexRobot: 800000,
  numRobots: 2,
  depreciationPeriod: 10,
  maintenancePercent: 3,
  robotPicksPerMin: 20,
  robotKgPerPick: 2,
  sortersReplace: 2,
  s10SortersCount: 2,
  hoursMode: 'weekly',
  extraHours: 4,
};

/** Hard-coded in the simulator: picks per minute of a manual sorter. */
export const HUMAN_PICKS_PER_MIN = 25;

/** The simulator's payback when the robots make no profit. */
export const NO_PAYBACK = 999;

/** The inputs as numbers, read as the simulator's getValues() did. */
export interface RoiValues {
  annualTonnage: number;
  disposalCost: number;
  operatingDays: number;
  hoursPerDay: number;
  sortorsPerShift: number;
  pickerCost: number;
  humanPicksPerMin: number;
  totalSorterTonsInput: number;
  capexRobot: number;
  numRobots: number;
  depreciationPeriod: number;
  /** A fraction: the field holds a percentage. */
  maintenancePercent: number;
  robotPicksPerMin: number;
  robotKgPerPick: number;
  sortersReplace: number;
  extraHours: number;
  s10SortersCount: number;
  hoursMode: RoiHoursMode;
}

export function roiValues(inputs: Partial<RoiInputs>): RoiValues {
  const all = { ...ROI_DEFAULTS, ...inputs };
  const n = (k: RoiNumberInput) => parseFloat(String(all[k])) || 0;
  return {
    annualTonnage: n('annualTonnage'),
    disposalCost: n('disposalCost'),
    operatingDays: n('operatingDays'),
    hoursPerDay: n('hoursPerDay'),
    sortorsPerShift: n('sortorsPerShift'),
    pickerCost: n('pickerCost'),
    humanPicksPerMin: HUMAN_PICKS_PER_MIN,
    totalSorterTonsInput: n('totalSorterTonsInput'),
    capexRobot: n('capexRobot'),
    numRobots: n('numRobots'),
    depreciationPeriod: n('depreciationPeriod'),
    maintenancePercent: n('maintenancePercent') / 100,
    robotPicksPerMin: n('robotPicksPerMin'),
    robotKgPerPick: n('robotKgPerPick'),
    sortersReplace: n('sortersReplace'),
    extraHours: n('extraHours'),
    s10SortersCount: n('s10SortersCount'),
    hoursMode: all.hoursMode === 'total' ? 'total' : 'weekly',
  };
}

/** One column of section 6 (equal investment in robots or in human sorters). */
export interface RoiInvestment {
  tons: number;
  revenue: number;
  profit: number;
  margin: number;
  ratio: number;
}

/** One column of section 7 (Add robots, Replace sorters). */
export interface RoiStrategy {
  sorters: number;
  robots: number;
  sortersTons: number;
  robotsTons: number;
  totalTons: number;
  vsStatusPct: number;
  salaries: number;
  robotsCost: number;
  totalCost: number;
  revenue: number;
  profit: number;
  profitVsPct: number;
}

export interface RoiResult {
  values: RoiValues;
  // Section 2: client results, manual sorters
  humanKgPerPick: number;
  tonsPerSorterYear: number;
  tonsPerSorterDay: number;
  totalSorterTons: number;
  disposalCostTotal: number;
  sorterRevenue: number;
  sorterProfit: number;
  sorterMargin: number;
  sorterSalariesTotal: number;
  // Section 4: robot results
  tonsPerRobotDay: number;
  tonsPerRobotYear: number;
  totalRobotTons: number;
  robotDisposalAvoid: number;
  annualDepreciation: number;
  annualMaintenance: number;
  annualOpCost: number;
  robotAnnualProfit: number;
  robotMargin: number;
  // Section 5: payback and ROI
  /** Years; NO_PAYBACK (999) when the robots make no profit. */
  payback: number;
  roiYear1: number;
  roiYear5: number;
  /** ROI over N years, N being the depreciation period (replaces "Year 10 ROI"). */
  roiOverYears: number;
  // Section 6: $100,000 (or 100 000 EUR) invested
  investmentAmount: number;
  robotsFor100k: RoiInvestment;
  humansFor100k: RoiInvestment;
  // Section 7: compare strategies
  addRobots: RoiStrategy;
  replaceSorters: RoiStrategy & { salarySavings: number };
  // Section 8: extended hours or reruns
  extendedHours: {
    sorters: number;
    robots: number;
    extraHoursPerYear: number;
    sortersExtra: number;
    robotsExtra: number;
    totalExtra: number;
    vsStatusPct: number;
    salaries: number;
    robotsCost: number;
    totalCost: number;
    revenue: number;
    profit: number;
    profitVsPct: number;
  };
}

/**
 * The simulator's updateDisplay(), without the page. The names and the order
 * of the steps are the simulator's, so the two can be read side by side.
 */
export function computeRoi(inputs: Partial<RoiInputs>): RoiResult {
  const v = roiValues(inputs);

  // ===== CLIENT CALCULATIONS =====
  const residualTons = v.annualTonnage;
  const totalSorters = v.sortorsPerShift;
  const totalSorterTons = v.totalSorterTonsInput;

  const tonsPerSorterYear = totalSorterTons / totalSorters;
  const tonsPerSorterDay = tonsPerSorterYear / v.operatingDays;
  const humanKgPerPick =
    totalSorters > 0
      ? (totalSorterTons * 1000) /
        (v.humanPicksPerMin * v.hoursPerDay * 60 * v.operatingDays * totalSorters)
      : 0;

  const remainingDisposal = residualTons - totalSorterTons;
  const disposalCostTotal = remainingDisposal * v.disposalCost;
  const sorterRevenue = totalSorterTons * v.disposalCost;
  const sorterProfit = sorterRevenue - totalSorters * v.pickerCost;
  const sorterMargin = sorterRevenue > 0 ? (sorterProfit / sorterRevenue) * 100 : 0;
  const sorterSalariesTotal = totalSorters * v.pickerCost;

  // ===== ROBOT CALCULATIONS =====
  const minutesPerDay = v.hoursPerDay * 60;
  const picksPerRobotDay = v.robotPicksPerMin * minutesPerDay;
  const tonsPerRobotDay = (picksPerRobotDay * v.robotKgPerPick) / 1000;
  const tonsPerRobotYear = tonsPerRobotDay * v.operatingDays;
  const totalRobotTons = tonsPerRobotYear * v.numRobots;
  const robotDisposalAvoid = totalRobotTons * v.disposalCost;

  const annualDepreciation = v.capexRobot / v.depreciationPeriod;
  const annualMaintenance = v.capexRobot * v.maintenancePercent;
  const annualOpCost = annualDepreciation + annualMaintenance;

  // ===== ROBOT ONLY PERFORMANCE =====
  const robotAnnualProfit = robotDisposalAvoid - annualOpCost;
  const payback = robotAnnualProfit > 0 ? v.capexRobot / robotAnnualProfit : NO_PAYBACK;

  // Year 1 ROI = Annual Profit / Capex
  const roiYear1 = (robotAnnualProfit / v.capexRobot) * 100;

  // Year 5 ROI = (Annual Profit × 5) / (Capex + Opex × 5)
  const totalCostYear5 = v.capexRobot + annualMaintenance * 5;
  const roiYear5 = ((robotAnnualProfit * 5) / totalCostYear5) * 100;

  // ROI over N years = (Annual Profit × N) / (Capex + Opex × N), N being the
  // depreciation period. The simulator multiplied the profit by 10 whatever N
  // was (decision of Oct 28, 2026: N on both sides).
  const years = v.depreciationPeriod;
  const totalCostOverYears = v.capexRobot + annualMaintenance * years;
  const roiOverYears = ((robotAnnualProfit * years) / totalCostOverYears) * 100;

  const robotMargin = robotDisposalAvoid > 0 ? (robotAnnualProfit / robotDisposalAvoid) * 100 : 0;

  // ===== SECTION 6: EQUAL $100K INVESTMENT COMPARISON =====
  const investmentAmount = 100000;

  const humanSalariesFull = totalSorters * v.pickerCost;
  const humanScaleFactor = investmentAmount / humanSalariesFull;
  const humanTonsFor100k = totalSorterTons * humanScaleFactor;
  const humanRevFor100k = humanTonsFor100k * v.disposalCost;
  const humanCostFor100k = investmentAmount;
  const humanProfitFor100k = humanRevFor100k - humanCostFor100k;
  const humanMarginFor100k = humanRevFor100k > 0 ? (humanProfitFor100k / humanRevFor100k) * 100 : 0;
  const humanRatioFor100k = humanCostFor100k > 0 ? humanProfitFor100k / humanCostFor100k : 0;

  // Robots: performance based on the annual operating cost. (The simulator
  // also computed a number of robots for the amount, never shown: left out.)
  const robotPerformanceScaleFactor = investmentAmount / annualOpCost;
  const robotTonsFor100k = totalRobotTons * robotPerformanceScaleFactor;
  const robotRevFor100k = robotTonsFor100k * v.disposalCost;
  const robotCostFor100k = investmentAmount;
  const robotProfitFor100k = robotRevFor100k - robotCostFor100k;
  const robotMarginFor100k = robotRevFor100k > 0 ? (robotProfitFor100k / robotRevFor100k) * 100 : 0;
  const robotRatioFor100k = robotCostFor100k > 0 ? robotProfitFor100k / robotCostFor100k : 0;

  // ===== SCENARIO 1: ADD ROBOTS =====
  const s1SortersTri = totalSorterTons;
  const s1RobotsTri = totalRobotTons;
  const s1TotalTri = s1SortersTri + s1RobotsTri;
  const s1VsStatusPct =
    totalSorterTons > 0 ? ((s1TotalTri - totalSorterTons) / totalSorterTons) * 100 : 0;
  const s1Salaries = totalSorters * v.pickerCost;
  const s1RobotCost = annualOpCost;
  const s1TotalCost = s1Salaries + s1RobotCost;
  const s1Revenue = s1TotalTri * v.disposalCost;
  const s1Profit = s1Revenue - s1TotalCost;
  const s1ProfitVsPct = sorterProfit > 0 ? ((s1Profit - sorterProfit) / sorterProfit) * 100 : 0;

  // ===== SCENARIO 2: REPLACE SORTERS =====
  const remainingSorters = totalSorters - v.sortersReplace;
  const s2SortersTri = tonsPerSorterYear * remainingSorters;
  const s2RobotsTri = totalRobotTons;
  const s2TotalTri = s2SortersTri + s2RobotsTri;
  const s2VsStatusPct =
    totalSorterTons > 0 ? ((s2TotalTri - totalSorterTons) / totalSorterTons) * 100 : 0;
  const s2Salaries = remainingSorters * v.pickerCost;
  const s2RobotCost = annualOpCost;
  const s2TotalCost = s2Salaries + s2RobotCost;
  const s2Revenue = s2TotalTri * v.disposalCost;
  const s2Profit = s2Revenue - s2TotalCost;
  const s2ProfitVsPct = sorterProfit > 0 ? ((s2Profit - sorterProfit) / sorterProfit) * 100 : 0;
  const s2SalarySavings = v.sortersReplace * v.pickerCost;

  // ===== SCENARIO 3: EXTENDED HOURS (section 8) =====
  const s3BaseSorters = v.s10SortersCount;
  const s3BaseRobots = v.numRobots;
  const s3BaseSorterTons = totalSorterTons;
  const s3BaseRobotTons = totalRobotTons;

  const hourlyRate = v.pickerCost / (v.operatingDays * v.hoursPerDay);
  const extraHoursPerYear = v.hoursMode === 'weekly' ? v.extraHours * 52 : v.extraHours;

  const sortersHourlyThroughput =
    s3BaseSorterTons / (v.operatingDays * v.hoursPerDay * totalSorters);
  const s3SortersExtra = sortersHourlyThroughput * s3BaseSorters * extraHoursPerYear;

  const robotsHourlyThroughput = s3BaseRobotTons / (v.operatingDays * v.hoursPerDay * s3BaseRobots);
  const s3RobotsExtra = robotsHourlyThroughput * s3BaseRobots * extraHoursPerYear;

  const s3TotalExtra = s3SortersExtra + s3RobotsExtra;
  const s3StatusQuoBase = s1TotalTri;
  const s3VsStatusPct = s3StatusQuoBase > 0 ? (s3TotalExtra / s3StatusQuoBase) * 100 : 0;

  // Extra hours at the hourly rate plus 30% overtime; no extra robot cost.
  const s3Salaries = extraHoursPerYear * s3BaseSorters * hourlyRate * 1.3;
  const s3RobotCost = 0;
  const s3TotalCost = s3Salaries + s3RobotCost;
  const s3Revenue = s3TotalExtra * v.disposalCost;
  const s3Profit = s3Revenue - s3TotalCost;
  const s3StatusQuoProfit = s1Profit;
  const s3ProfitVsPct = s3StatusQuoProfit > 0 ? (s3Profit / s3StatusQuoProfit) * 100 : 0;

  return {
    values: v,
    humanKgPerPick,
    tonsPerSorterYear,
    tonsPerSorterDay,
    totalSorterTons,
    disposalCostTotal,
    sorterRevenue,
    sorterProfit,
    sorterMargin,
    sorterSalariesTotal,
    tonsPerRobotDay,
    tonsPerRobotYear,
    totalRobotTons,
    robotDisposalAvoid,
    annualDepreciation,
    annualMaintenance,
    annualOpCost,
    robotAnnualProfit,
    robotMargin,
    payback,
    roiYear1,
    roiYear5,
    roiOverYears,
    investmentAmount,
    robotsFor100k: {
      tons: robotTonsFor100k,
      revenue: robotRevFor100k,
      profit: robotProfitFor100k,
      margin: robotMarginFor100k,
      ratio: robotRatioFor100k,
    },
    humansFor100k: {
      tons: humanTonsFor100k,
      revenue: humanRevFor100k,
      profit: humanProfitFor100k,
      margin: humanMarginFor100k,
      ratio: humanRatioFor100k,
    },
    addRobots: {
      sorters: totalSorters,
      robots: v.numRobots,
      sortersTons: s1SortersTri,
      robotsTons: s1RobotsTri,
      totalTons: s1TotalTri,
      vsStatusPct: s1VsStatusPct,
      salaries: s1Salaries,
      robotsCost: s1RobotCost,
      totalCost: s1TotalCost,
      revenue: s1Revenue,
      profit: s1Profit,
      profitVsPct: s1ProfitVsPct,
    },
    replaceSorters: {
      sorters: remainingSorters,
      robots: v.numRobots,
      sortersTons: s2SortersTri,
      robotsTons: s2RobotsTri,
      totalTons: s2TotalTri,
      vsStatusPct: s2VsStatusPct,
      salaries: s2Salaries,
      robotsCost: s2RobotCost,
      totalCost: s2TotalCost,
      revenue: s2Revenue,
      profit: s2Profit,
      profitVsPct: s2ProfitVsPct,
      salarySavings: s2SalarySavings,
    },
    extendedHours: {
      sorters: s3BaseSorters,
      robots: v.numRobots,
      extraHoursPerYear,
      sortersExtra: s3SortersExtra,
      robotsExtra: s3RobotsExtra,
      totalExtra: s3TotalExtra,
      vsStatusPct: s3VsStatusPct,
      salaries: s3Salaries,
      robotsCost: s3RobotCost,
      totalCost: s3TotalCost,
      revenue: s3Revenue,
      profit: s3Profit,
      profitVsPct: s3ProfitVsPct,
    },
  };
}

/** Whether the robots pay back (the simulator showed 999.00 otherwise). */
export function hasPayback(result: RoiResult): boolean {
  return result.payback !== NO_PAYBACK && Number.isFinite(result.payback);
}
